import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";

const EditableCell = ({ getValue, row, column, table, isMulti, options: directOptions, dropdownWidth }) => {
    const rawValue = getValue();
    const initialValue = (rawValue && typeof rawValue === 'object' && rawValue.value)
        ? rawValue.value
        : rawValue;
    const [value, setValue] = useState(initialValue);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const saveOnBlur = async (blurEvent) => {
        const shouldSkipBlurSave = table.options.meta?.shouldSkipBlurSave;
        const skipBlurSave = Boolean(
            typeof shouldSkipBlurSave === "function"
                ? shouldSkipBlurSave({ row, column, value, initialValue, blurEvent })
                : false
        );

        if (skipBlurSave) {
            return;
        }

        const hasChanged = value !== initialValue;
        const shouldValidateUnchangedOnBlur = table.options.meta?.shouldValidateUnchangedOnBlur;
        const validateUnchangedOnBlur = Boolean(
            typeof shouldValidateUnchangedOnBlur === "function"
                ? shouldValidateUnchangedOnBlur({ row, column, value, initialValue })
                : false
        );

        if (hasChanged || validateUnchangedOnBlur) {
            if (hasChanged) {
                table.options.meta?.updateData(row.index, column.id, value);
            }

            const result = await table.options.meta?.handleSave({
                row,
                column,
                value: value,
            });

            const shouldRevertOnFailedSave = Boolean(table.options.meta?.revertOnFailedSave === true);

            if (result === false && shouldRevertOnFailedSave) {
                setValue(initialValue);
            }
        }
    };

    const commonStyle = {
        fontSize: '12px',
        height: '31px',
    };

    const focusStyle = isFocused
        ? {
            border: '1px solid #2b6cb0',
            boxShadow: '0 0 0 2px rgba(43, 108, 176, 0.2)',
            backgroundColor: '#fff',
        }
        : {
            border: '1px solid transparent',
            boxShadow: 'none',
            backgroundColor: 'transparent',
        };

    const inputContainerStyle = {
        ...focusStyle,
        borderRadius: '4px',
        transition: 'all 120ms ease',
    };

    const neutralContainerStyle = {
        border: '1px solid transparent',
        boxShadow: 'none',
        backgroundColor: 'transparent',
        borderRadius: '4px',
    };

    const dropdownOptions = directOptions || column.columnDef.meta?.options;
    const useClearableSingleSelect = Boolean(column.columnDef.meta?.useClearableSingleSelect);
    const saveOnBlurOnly = Boolean(column.columnDef.meta?.saveOnBlurOnly);
    const isExplicitSelect = column.columnDef.meta?.type === "select";

    // --- 1. Render Logic: Multi-Select ---
    if (isMulti && dropdownOptions) {
        const getMultiValue = () => {
            if (!value || typeof value !== 'string') return [];
            return value.split(';').filter(v => v).map(v => ({ label: v, value: v }));
        };

        return (
            <div className="editable-cell-container" style={neutralContainerStyle}>
                <Select
                    isMulti
                    options={dropdownOptions}
                    value={getMultiValue()}
                    onChange={(selected) => {
                        const newValue = selected ? selected.map(s => s.value).join(';') : "";
                        setValue(newValue);
                        table.options.meta?.updateData(row.index, column.id, newValue);
                        table.options.meta?.handleSave({ row, column, value: newValue });
                    }}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                            ...base,
                            minHeight: '31px',
                            height: 'auto',
                            fontSize: '12px',
                            borderRadius: '2px',
                            borderColor: '#ccc',
                            boxShadow: 'none',
                            backgroundColor: '#fff'
                        }),
                        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
                        input: (base) => ({ ...base, margin: '0', fontSize: '12px' }),
                    }}
                />
            </div>
        );
    }

    // --- 2. Render Logic: Dropdown (Clearable Single Select) ---
    if (useClearableSingleSelect && dropdownOptions && Array.isArray(dropdownOptions) && dropdownOptions.length > 0) {
        const selectedOption = dropdownOptions.find(
            (opt) => String(opt?.value ?? "") === String(value ?? "")
        ) || null;

        return (
            <div className="editable-cell-container" style={neutralContainerStyle}>
                <Select
                    isClearable
                    options={dropdownOptions}
                    value={selectedOption}
                    onChange={(selected) => {
                        const newValue = selected?.value ?? "";
                        setValue(newValue);

                        if (!saveOnBlurOnly) {
                            table.options.meta?.updateData(row.index, column.id, newValue);
                            table.options.meta?.handleSave({ row, column, value: newValue });
                        }
                    }}
                    onBlur={async () => {
                        if (saveOnBlurOnly) {
                            await saveOnBlur();
                        }
                    }}
                    placeholder={column.columnDef.meta?.blankOptionLabel ?? ""}
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                            ...base,
                            minHeight: '31px',
                            height: '31px',
                            fontSize: '12px',
                            borderRadius: '2px',
                            borderColor: 'transparent',
                            boxShadow: 'none',
                            backgroundColor: 'transparent'
                        }),
                        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
                        input: (base) => ({ ...base, margin: '0', fontSize: '12px' }),
                        indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                    }}
                />
            </div>
        );
    }

    // --- 3. Render Logic: Dropdown (Select Box) ---
    if (isExplicitSelect || (dropdownOptions && Array.isArray(dropdownOptions) && dropdownOptions.length > 0)) {
        const selectOptions = Array.isArray(dropdownOptions) ? dropdownOptions : [];

        return (
            <div style={neutralContainerStyle}>
                <Form.Select
                    value={value ?? ""}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        setValue(newValue);
                        if (!saveOnBlurOnly) {
                            table.options.meta?.updateData(row.index, column.id, newValue);
                            table.options.meta?.handleSave({ row, column, value: newValue });
                        }
                    }}
                    onBlur={async () => {
                        if (saveOnBlurOnly) {
                            await saveOnBlur();
                        }
                    }}
                    className="table-dropdown-box form-control-sm"
                    style={{
                        ...commonStyle,
                        width: dropdownWidth || '100%',
                        marginLeft: '0',
                        marginRight: 'auto',
                        border: 'none',
                        backgroundColor: 'transparent'
                        // paddingTop: '2px',
                        // paddingBottom: '2px'
                    }}
                >
                    {!column.columnDef.meta?.noBlankOption && (
                        <option value="">{column.columnDef.meta?.blankOptionLabel ?? "Select..."}</option>
                    )}
                    {selectOptions.map((opt, idx) => {
                        const displayLabel = opt.label || opt.region_code || Object.values(opt)[0];
                        const displayValue = opt.value || opt.region_code || Object.values(opt)[0];

                        return (
                            <option key={idx} value={String(displayValue)}>
                                {String(displayLabel)}
                            </option>
                        );
                    })}
                </Form.Select>
            </div>
        );
    }

    // --- 4. Render Logic: Checkbox ---
    const isCheckbox = column.columnDef.meta?.type === "checkbox" || typeof initialValue === "boolean";

    if (isCheckbox) {
        return (
            <div style={{
                ...neutralContainerStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '31px'
            }}>
                <input
                    type="checkbox"
                    className="form-check-input"
                    style={{
                        width: '16px',
                        height: '16px',
                        margin: '0',
                        cursor: 'pointer',
                        padding: '0'
                    }}
                    checked={!!value}
                    onChange={(e) => {
                        const newValue = e.target.checked;
                        setValue(newValue);
                        table.options.meta?.updateData(row.index, column.id, newValue);
                        table.options.meta?.handleSave({ row, column, value: newValue });
                    }}
                />
            </div>
        );
    }

    // --- 5. Render Logic: Date Picker ---
    if (column.id.toLowerCase().includes("date")) {
        return (
            <div style={neutralContainerStyle}>
                <DatePicker
                    selected={(value && !isNaN(new Date(value))) ? new Date(value) : null}
                    onChange={(date) => {
                        setValue(date);
                        table.options.meta?.updateData(row.index, column.id, date);
                        table.options.meta?.handleSave({ row, column, value: date });
                    }}
                    dateFormat="dd/MM/yyyy"
                    autoComplete="off"
                    style={{ backgroundColor: 'transparent' }}
                    className="no-border form-control form-control-sm"
                    portalId="root"
                    popperClassName="editable-cell-datepicker-popper"
                    popperProps={{ strategy: "fixed" }}
                    placeholderText="DD/MM/YYYY"
                // customInput={<input style={{ ...commonStyle, border: 'none', background: 'white' }} />}
                />
            </div>
        );
    }

    // --- 6. Render Logic: Share / Percentage Field ---
    const isShare = column.id === "share" || column.columnDef.meta?.suffix === "%";

    if (isShare) {
        return (
            <div
                className="d-flex align-items-center justify-content-end w-100"
                style={{ ...commonStyle, ...inputContainerStyle, paddingRight: '10px' }}
            >
                <input
                    value={value ?? ""}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={async (e) => {
                        setIsFocused(false);
                        await saveOnBlur(e);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && saveOnBlur()}
                    type="text"
                    className="no-border"
                    style={{
                        fontSize: '12px',
                        border: 'none',
                        outline: 'none',
                        textAlign: 'right',
                        width: '100%',
                        padding: 0,
                        backgroundColor: 'transparent'
                    }}
                />
                <span style={{
                    fontSize: '12px',
                    color: '#000',
                    marginLeft: '0px',
                    userSelect: 'none'
                }}>
                    %
                </span>
            </div>
        );
    }

    // --- 6. Render Logic: Textarea ---
    const inputType = column.columnDef.meta?.inputType;
    const isTextarea = inputType === "textarea";
    const isNumberInput = inputType === "number";
    const textareaRows = column.columnDef.meta?.rows || 3;
    const textareaResize = column.columnDef.meta?.resize || "none";
    const isReadOnly = column.columnDef.meta?.readOnly === true;
    const numberStep = column.columnDef.meta?.step ?? 1;
    const numberMin = column.columnDef.meta?.min;
    const numberMax = column.columnDef.meta?.max;

    if (isTextarea) {
        return (
            <div style={inputContainerStyle} className="editable-cell-container">
                <Form.Control
                    as="textarea"
                    value={value ?? ""}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        setValue(newValue);
                    }}
                    onBlur={async (e) => {
                        setIsFocused(false);
                        if (!isReadOnly) {
                            await saveOnBlur(e);
                        }
                    }}
                    onFocus={() => setIsFocused(true)}
                    rows={textareaRows}
                    readOnly={isReadOnly}
                    className="no-border form-control-sm"
                    style={{
                        fontSize: '12px',
                        border: 'none',
                        backgroundColor: isReadOnly ? '#f5f5f5' : 'transparent',
                        resize: textareaResize,
                        minHeight: `${(textareaRows * 1.5) + 0.5}rem`
                    }}
                />
            </div>
        );
    }
    // --- 7. Render Logic: Explicit Number Input (with spinner arrows) ---
    if (isNumberInput) {
        return (
            <div style={inputContainerStyle}>
                <input
                    value={value ?? ""}
                    onChange={(e) => {
                        let newValue = e.target.value;

                        if (column.columnDef.meta?.numericOnly === true) {
                            newValue = newValue.replace(/[^0-9]/g, "");
                        }

                        setValue(newValue);
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={async (e) => {
                        setIsFocused(false);
                        await saveOnBlur(e);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && saveOnBlur()}
                    type="number"
                    inputMode="numeric"
                    className="no-border form-control"
                    style={{ ...commonStyle, border: 'none', backgroundColor: 'transparent' }}
                    step={numberStep}
                    {...(numberMin !== undefined ? { min: numberMin } : {})}
                    {...(numberMax !== undefined ? { max: numberMax } : {})}
                />
            </div>
        );
    }
    // --- 8. Render Logic: Default Text/Number Input ---
    const maxLength = column.columnDef.meta?.maxLength;
    const isNumericOnly = column.columnDef.meta?.numericOnly === true;
    const shouldUppercase = column.columnDef.meta?.uppercase === true;

    return (
        <div style={inputContainerStyle}>
            <input
                value={value ?? ""}
                onChange={(e) => {
                    let newValue = e.target.value;

                    if (isNumericOnly) {
                        newValue = newValue.replace(/[^0-9]/g, "");
                    }

                    if (shouldUppercase) {
                        newValue = newValue.toUpperCase();
                    }

                    setValue(newValue);
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={async (e) => {
                    setIsFocused(false);
                    await saveOnBlur(e);
                }}
                onKeyDown={(e) => e.key === 'Enter' && saveOnBlur()}
                type={isNumericOnly ? "text" : (typeof initialValue === "number" ? "number" : "text")}
                inputMode={isNumericOnly ? "numeric" : undefined}
                className="no-border form-control"
                style={{ ...commonStyle, border: 'none', backgroundColor: 'transparent' }}
                {...(maxLength !== undefined ? { maxLength } : {})}
            />
        </div>
    );
};

export default React.memo(EditableCell);
